import { Injectable } from "@angular/core";
import { GridLayoutService } from "@irb/irb-common/interfaces/grid-layout.service";
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { InventoryComponent } from "@irb/irb-orders/order-blotter/inventory/inventory.component";
import { UtilityService } from "@irb-common/services/utility.service";
import { AppService } from "@irb/app.service";
import { InventoryColInfoService } from "@irb/irb-orders/order-blotter/inventory/inventory-col-info.service";
import { CellrenderInfoService } from "./cellrender-info.service";
import { InventoryService } from "./inventory.service";
import { IrbClientsEvents } from "@irb/irb-clients/irb-clients.events";
import { OrderStatusActionMenu } from "./order-status-action-menu";
import { TradeDetailsSymbolRendererComponent } from "@irb/irb-editors/edit-trading-instructions/trade-details/trade-details-symbol.renderer.component";
import { formatOrderStatus, formatTicketType, formatTransaction } from "@irb-common/formatters";
import { ColDef, ColGroupDef } from 'ag-grid-community';
import { IDropdownItem } from '@irb/irb-common/models/dropdown.model';
import { DropdownFilterRendererComponent } from '@irb/irb-common/components/grid/renderers/dropdown-filter.cell.component';
import { IrbAgSetColumnFilterComponent } from "@irb-common/components/irebal-ag-grid-ent-avatar/irb-ag-set-column-filter/irb-ag-set-filter.component";
import { CustomFilterComponent } from "@irb/irb-orders/order-blotter/inventory/custom-filter/custom-filter.component";

@Injectable({
  providedIn: "root",
})
export class InventoryGridLayoutService implements GridLayoutService<InventoryComponent> {
    httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT'
      })
    };
    orderStatusList: string[] = [];
    advisorList: string[] = [];
    submittedByList: string[] = [];
    transTypeList: string[] = [];
    securityTypeList: string[] = [];
    ticketTypeList: string[] = [];
    parentComp:InventoryComponent;

    constructor(private util: UtilityService, 
      private as: AppService,
      private inventoryColInfoService:InventoryColInfoService,
      public http: HttpClient,
      private cellrendererService:CellrenderInfoService,
      private readonly pfs: InventoryService,
      private orderStatusActionMenu:OrderStatusActionMenu,
      ) {
    }

    current = 'activeOrders';

    init(current: string) {
      this.current = current;
    }

    initFilters() {
      return new Promise((resolve: any) => {
        this.getOrderStatuses();
        this.getTransTypes();
        this.getTicketTypes();

        this.getAdvisors()
          .then(() => this.getSubmittedBys())
          .then(() => this.getSecurityTypes())
          .then(resolve());        
      });
    }

    getFrameworkComponents() {
      return {
        "DropdownFilterRendererComponent": DropdownFilterRendererComponent,
        "customFilterToolPanel": CustomFilterComponent,
        "irbAgSetColumnFilterComponent": IrbAgSetColumnFilterComponent
      };
    }

    getColumnDefs(parent: InventoryComponent): Array<any> {
      this.parentComp = parent;
      let cols = [];

      cols.push(this.getStaticColumnGroup(parent));
      cols.push(this.getInitialColumnGroup());
      
      if(this.current != "automatedOrders") {
        cols.push(this.getTradeDetailsColumnGroup());
        cols.push(this.getTradeInstructionsColumnGroup());
        cols.push(this.getQuoteColumnGroup());
        cols.push(this.getTradingVolumeColumnGroup());
        cols.push(this.getSecurityDetailColumnGroup());  
        cols.push(this.getReinvestmentColumnGroup());
        cols.push(this.getTradeAuthorizationColumnGroup());
        cols.push(this.getHistoryColumnGroup());
        cols.push(this.getPortfolioColumnGroup());  
      } else {
        cols.push(this.getHistoryColumnGroup());
        cols.push(this.getPortfolioColumnGroup()); 
        cols.push(this.getTradeInstructionsColumnGroup());         
        cols.push(this.getTradeDetailsColumnGroup());        
        cols.push(this.getQuoteColumnGroup());
        cols.push(this.getSecurityDetailColumnGroup()); 
        cols.push(this.getTradingVolumeColumnGroup());        
      }

      return cols.concat(this.getHiddenColumns());
    }

    getStaticColumnGroup(parent: InventoryComponent): ColGroupDef {
      return {
        headerName: '',
        suppressFiltersToolPanel: true,
        children: [
          {
            headerName: 'Select All',
            colId:'selectAll',
            headerComponent: 'customHeader',
            cellRenderer: 'customHeader',
            cellRendererParams: { parentObject: parent, type: 'rowCheckBox' },
            suppressColumnsToolPanel: true,
            suppressFiltersToolPanel: true,
            suppressSizeToFit: true,
            toolPanelClass: 'toolpanel-header-checkbox-hide',
            headerComponentParams: { parentObject: parent, type: 'headerCheckBox' },
            floatingFilterComponent: 'customHeader',
            floatingFilterComponentParams: { parentObject: parent, type: 'NoClearAllBtn'},
            suppressFloatingFilterButton: true,
            pinned: 'left',
            suppressMovable: true,
            lockPosition: true,
            lockVisible: true,
            sortable: false,
            filter: true,
            resizable: true,
            minWidth: 25,
            width: 25,
            maxWidth:25,
            cellStyle: { 'padding': '5px 3px 0 2px' },
            headerClass: 'customHeaderClassSelectAll',
            cellClass: 'selectAllCheckBoxRecord'
          },{
            headerName: 'Account',
            pinned: 'left',
            suppressMovable: true,
            lockPosition: true,
            lockVisible: true,
            suppressFiltersToolPanel: false,
            field: 'accountNumber',
            colId:'accountNumber',
            cellRendererSelector:this.cellrendererService.cellRendererSelectorBlockOrders.bind(this,parent),
            cellClass: 'irb-cell-content-right',
            minWidth: 95,
            filter: 'agTextColumnFilter',
            suppressFloatingFilterButton: true,            
            filterParams: {
              // applyButton: true,
              clearButton: true,
              debounceMs: 1000,
              // filterOptions: [],
              textFormatter: this.textFilterFormatter,
              suppressAndOrCondition: true,
              // values: (params: any) => {
              //    params.success(this.modelNames);
              // }
            },
          },{
            headerName: 'Account Name',
            pinned: 'left',
              suppressMovable: true,
              lockPosition: true,
              lockVisible: true,
            suppressFiltersToolPanel: false,
            field: 'accountName',
            colId:'accountName', 
            cellRendererSelector:this.cellrendererService.cellRendererSelectorAccountName.bind(this),
            minWidth: 130,
            filter: 'agTextColumnFilter',
            suppressFloatingFilterButton: true,
            filterParams: {
              clearButton: true,
              debounceMs: 1000,
              textFormatter: this.textFilterFormatter,
              suppressAndOrCondition: true,
            },
          },{
            headerName: 'Symbol',
            pinned: 'left',
            suppressMovable: true,
            lockPosition: true,
            lockVisible: true,
            suppressFiltersToolPanel: false,
            field: 'securitySymbol',
            colId:'securitySymbol',
            minWidth: 68,
            width:100,
            filter: 'agTextColumnFilter',
            cellRenderer: TradeDetailsSymbolRendererComponent,
            suppressFloatingFilterButton: true,
            filterParams: {
              clearButton: true,
              debounceMs: 1000,
              textFormatter: this.textFilterFormatter,
              suppressAndOrCondition: true,
            },
            cellClass: ['symbol-no-pointer']
        },{
          headerName: 'Order Status',
          pinned: 'left',
              suppressMovable: true,
              lockPosition: true,
              lockVisible: true,
          headerComponentParams:{
            irbColInfo: this.inventoryColInfoService.orderStatusColInfo()
          },
          suppressFiltersToolPanel: false,
          field: 'orderStatus',
          colId:'orderStatus',
          cellClassRules: {
            'orderMarkedAsExecuted': (params:any) => { return (params && params.data && params.data.orderStatus === "APPROVED"
                    && params.data.wasOrderMarkAsExecuted)},
            'orderMarkedAsNotExecuted': (params:any) => { return (params && params.data && params.data.orderStatus === "APPROVED"
                    && !params.data.wasOrderMarkAsExecuted)},
            'orderWarning': (params:any) => { return (params && params.data && params.data.orderStatus === "WARNING" )},
            'orderError': (params:any) => { return (params && params.data && params.data.orderStatus === "ERROR" )}
          },
          cellRendererSelector: this.orderStatusRender.bind(this),
          minWidth: 130,
          filter: 'agSetColumnFilter',
          filterParams: {
              values: (params: any) => {
                params.success(this.orderStatusList);
              },
              buttons:['reset', 'apply'],
              newRowsAction: 'keep',
              suppressSorting: true,
              cellHeight: 30,
              closeOnApply: true,              
              refreshValuesOnOpen: true
          }
        }
      ]} as ColGroupDef;
    }

    getInitialColumnGroup(): ColGroupDef {
      return {
        headerName: '',
        suppressFiltersToolPanel: true,
        children: [
          {
            headerName: 'Trade Message',
            suppressFiltersToolPanel: true,
            field: 'tradeMessage',
            colId: 'tradeMessage',
            cellRenderer: 'cellRendererFormatter',
            cellRendererParams: {
              tooltipValueFormatter: (params: any) => {
                if(params.value) {
                  const f = params.value.split(/\|/g).slice(0,5).map((s:string) => s.trim()).join('<hr style="height:1px;border:none;"/>');
                  return f;
                } return params.value;
              }
            },
            minWidth: 180
          },
          {
            headerName: 'Order ID',
            suppressFiltersToolPanel: false,
            field: 'veoOrderID',
            colId: 'veoOrderID',
            cellRenderer: 'cellRendererFormatter',
            cellClass: 'irb-cell-content-right',
            minWidth: 75,
            filter: 'agTextColumnFilter',
            suppressFloatingFilterButton: true,
            filterParams: {
              filterOptions: ['contains'],
              clearButton: false,
              debounceMs: 1000,
              textFormatter: this.textFilterFormatter,
              suppressAndOrCondition: true,
            },
          }]
      };
    }

    getTradeDetailsColumnGroup(): ColGroupDef {
      return {
          headerName: 'Trade Details',
          suppressFiltersToolPanel: true,
          children: [
          {
            headerName: 'Trans Type',
            suppressFiltersToolPanel: false,
            field: 'transactionType',
            colId:'transactionType', 
            cellRendererSelector: (params: any) => {
              return this.cellrendererService.formatTransType(params);
            },
            minWidth: 120,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: (params: any) => {
                params.success(this.transTypeList);
              },
              valueFormatter: (params: any) => {
                return formatTransaction(params.value);
              },
              buttons:['reset', 'apply'],
              newRowsAction: 'keep',
              suppressSorting: true,
              cellHeight: 30,
              closeOnApply: true,              
              refreshValuesOnOpen: true
            },
          },
          {
            headerName: 'Amt Type',
            headerComponentParams:{
              irbColInfo: this.inventoryColInfoService.amtTypeColInfo()
            },
            suppressFiltersToolPanel: true,
            field: 'amountType',
            colId:'amountType', 
            minWidth: 75,
            cellClass: 'dropdownFilterCell',
            cellRenderer: 'DropdownFilterRendererComponent',
            cellRendererParams: (params: any) => {
              return {
                    defaultValue: {name: params.data.amountType, value: params.data.amountType},
                    values: [{name: 'Shares', value: 'Shares'},{name: 'Dollars', value: "Dollars"}],
                    placeholder: 'none',
                    disabled: !this.cellrendererService.isAmtTypeEditable(params),
                    onChange: (_params: any, _item: IDropdownItem) => {
                        _params.newValue = _item.value;
                        _params.oldValue = _params.data.amountType;
                        _params.data.amountType = _item.value;
                        this.parentComp.onCellValueChanged(_params);
                    }
                }
            }
          },
          {
            headerName: 'Shr Qty',
            headerComponentParams:{
              irbColInfo: this.inventoryColInfoService.shrQtyColInfo()
            },
            suppressFiltersToolPanel: true,
            sortable: true,
            field: 'quantity',
            colId:'quantity',
            cellRendererSelector: (params: any) => {
              return this.cellrendererService.formatShareQuantity(params);
            },
            cellClass: 'irb-cell-content-right',
            minWidth: 90,
          },
          {
            headerName: 'Order Amt',
            suppressFiltersToolPanel: true,
            sortable: true,
            field: 'dollarAmount',
            colId:'dollarAmount', 
            cellRenderer : 'cellRendererFormatter',
            cellRendererParams: { type: 'currency' },
            minWidth: 90,
          }
        ] 
      }
    }

    getTradeInstructionsColumnGroup(): ColGroupDef {
      return { 
          headerName: 'Trade Instructions',
          suppressFiltersToolPanel: true,
          children: [
          {
            headerName: 'Order Type',
            suppressFiltersToolPanel: true,
            field: 'orderType',
            colId:'orderType',
            cellRendererSelector: (params: any) => {
              return this.cellrendererService.cellRendererSelectorOrderType(params, this.parentComp);
            },
            minWidth: 95,
          },
          {
            headerName: 'Price',
            headerComponentParams:{
              irbColInfo: this.inventoryColInfoService.priceColInfo()
            },
            suppressFiltersToolPanel: true,
            field: 'price',
            colId:'price', 
            cellRendererSelector: (params: any) => {
              return this.cellrendererService.cellRendererSelectorPrice(params, this.parentComp);
            },
            cellClass: 'irb-cell-content-right',
            minWidth: 160,
            hide: this.current == 'automatedOrders',
            suppressColumnsToolPanel: this.current == 'automatedOrders'
          },
          {  
            headerName: 'Expiration Type',
            suppressFiltersToolPanel: true,
            field: 'expirationType',
            colId:'expirationType', 
            cellRendererSelector: (params: any) => {
              return this.cellrendererService.cellRendererSelectorExpirationType(params, this.parentComp);
            },
            minWidth: 80,
            hide: this.current == 'automatedOrders',
            suppressColumnsToolPanel: this.current == 'automatedOrders'
          },
          {
            headerName: 'Expiration Date',
            suppressFiltersToolPanel: true,
            field: 'expirationDate',
            colId:'expirationDate', 
            cellRendererSelector: (params: any) => {
              return this.cellrendererService.cellRendererSelectorExpirationDate(params, this.parentComp);
            },
            minWidth: 100,
            hide: this.current == 'automatedOrders',
            suppressColumnsToolPanel: this.current == 'automatedOrders'
          },
          {
            headerName: 'Not Held',
            headerComponentParams:{
              irbColInfo: this.inventoryColInfoService.notHeldColInfo()
            },
            suppressFiltersToolPanel: true,
            field: 'ordIsNotHeld',
            colId:'ordIsNotHeld', 
            cellRendererSelector: (params: any) => {
              return this.cellrendererService.cellRendererSelectorNotHeld(params, this.parentComp);
            },
            minWidth: 80,
            cellClass: 'center',
            hide: this.current == 'automatedOrders',
            suppressColumnsToolPanel: this.current == 'automatedOrders'
          }
        ] 
      };
    }

    getQuoteColumnGroup(): ColGroupDef {
      return {
        headerName: '',
        suppressFiltersToolPanel: true,
        children: [{
          headerName: 'Quote',
          headerComponentParams:{
            irbColInfo: this.inventoryColInfoService.quoteColInfo()
          },
          suppressFiltersToolPanel: true,
          sortable: false,
          field: 'currentUnitPrice',
          colId:'currentUnitPrice', 
          cellRenderer : 'cellRendererFormatter',
          cellRendererParams: { type: 'currency', displaySymbol: '$', decimalPosition: '2' },
          cellClass: 'irb-cell-content-right',
          minWidth: 100,
        }]
      };
    }

    getTradingVolumeColumnGroup(): ColGroupDef {
      return { 
          headerName: 'Trading Volume',
          suppressFiltersToolPanel: true,
          children: [
          {
            headerName: '10 Day Avg Vol',
            headerComponentParams:{
              irbColInfo: this.inventoryColInfoService.avgVolColInfo()
            },
            suppressFiltersToolPanel: true,
            field: 'avgDailyTradeVol',
            colId:'avgDailyTradeVol', 
            cellRenderer : 'cellRendererFormatter',
            cellClass: 'irb-cell-content-right',
            minWidth: 110,
          },
          {
            headerName: '% of Avg Vol',
            suppressFiltersToolPanel: true,
            field: 'perShrAmt',
            colId:'perShrAmt',
            cellRendererSelector: (params: any) => {
              return this.cellrendererService.tradingValueRenderer(params);
            },
            cellClass: 'irb-cell-content-right',
            minWidth: 75,
          }
        ] 
      };
    }

    getSecurityDetailColumnGroup(): ColGroupDef {
      return { 
          headerName: 'Security Detail',
          suppressFiltersToolPanel: true,
          children: [
          {
            headerName: 'Sec Type',
            suppressFiltersToolPanel: false,
            field: 'securityType',
            colId:'securityType', 
            cellRenderer : 'cellRendererFormatter',
            minWidth: 100,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: (params: any) => {
                params.success(this.securityTypeList);
              },
              buttons:['reset', 'apply'],
              newRowsAction: 'keep',
              suppressSorting: true,
              cellHeight: 30,
              closeOnApply: true,              
              refreshValuesOnOpen: true
            },
          },
          {
            headerName: 'Tkt Type',
            headerComponentParams:{
              irbColInfo: this.inventoryColInfoService.ticketTypeInfo()
            },
            suppressFiltersToolPanel: false,
            field: 'ticketType',
            colId:'ticketType', 
            cellRenderer : 'cellRendererFormatter',
            minWidth: 55,
            filter: 'agSetColumnFilter',
            filterParams: {
              values: (params: any) => {
                params.success(this.ticketTypeList);
              },
              valueFormatter: (params: any) => {
                return formatTicketType(params.value);
              },
              buttons:['reset', 'apply'],
              newRowsAction: 'keep',
              suppressSorting: true,
              cellHeight: 30,          
              refreshValuesOnOpen: true,
              closeOnApply: true
            },
          }
        ] 
      };
    }

    getReinvestmentColumnGroup(): ColGroupDef {
      return { 
          headerName: 'Reinvestment & Fees options',
          suppressFiltersToolPanel: true,
          children: [
          {
            headerName: 'Rvst DIVs',
            suppressFiltersToolPanel: true,
            field: 'isDividendReinvest',
            colId:'isDividendReinvest', 
            cellRendererSelector: (params: any) => {
              return this.cellrendererService.cellRendererSelectorRvstDIVs(params,this.parentComp);
            },
            minWidth: 50,
            cellClass: 'center'
          },
          {
            headerName: 'Rvst CGs',
            suppressFiltersToolPanel: true,
            field: 'isCapGainsReinvest',
            colId:'isCapGainsReinvest', 
            cellRendererSelector: (params: any) => {
              return this.cellrendererService.cellRendererSelectorRvstCGs(params,this.parentComp);
            },
            minWidth: 50,
            cellClass: 'center'
          },
          {
            headerName: 'Fees',
            suppressFiltersToolPanel: true,
            //cellClass: 'progTitle',
            field: 'feesOption',
            colId:'feesOption', 
            cellRendererSelector: (params: any) => {
              return this.cellrendererService.cellRendererSelectorFees(params)
            },        
            minWidth: 75
          }
        ] 
      };
    }

    getTradeAuthorizationColumnGroup(): ColGroupDef {
      return { 
          headerName: 'Trade Authorization',
          suppressFiltersToolPanel: true,
          children: [
          {
            headerName: 'Sol',
            suppressFiltersToolPanel: true,
            field: 'isSolicited',
            colId:'isSolicited', 
            cellRendererSelector: (params: any) => {
            return this.cellrendererService.cellRendererSelectorSol(params,this.parentComp)
            },
            minWidth: 50
          },
          {
            headerName: 'Disc',
            suppressFiltersToolPanel: true,
            field: 'isDiscretionary',
            colId:'isDiscretionary', 
            cellRendererSelector: (params: any) => {
            return this.cellrendererService.cellRendererSelectorDisc(params,this.parentComp)
            },
            minWidth: 50
          }
        ] 
      };
    }

    getHistoryColumnGroup(): ColGroupDef {
      return { 
          headerName: 'History',
          suppressFiltersToolPanel: true,
          children: [
          {
            headerName: 'Order History',
            suppressFiltersToolPanel: true,
            sortable: false,
            field: 'orderHistory',
            colId:'orderHistory',
            minWidth: 60,
            cellRendererSelector: (params: any) => {
              return this.cellrendererService.cellRendererSelectorOrderHistory(params,this.parentComp,false)
            }        
          },
          {
            headerName: 'Approval Date',
            suppressFiltersToolPanel: false,
            field: 'createdDate',
            colId:'createdDate', 
            cellRenderer : 'cellRendererFormatter',
            cellRendererParams: {
              type: 'date', format: 'MM/dd/yyyy h:mm a', displayETtext : true
            },
            minWidth: 100,
            filter: (this.current === 'activeOrders' || this.current === 'automatedOrders') ? 'rangeDatePicker': null,
            filterParams: {
              enableArchiveSearch: (this.current === 'resolvedOrders')
            }
          },
          {
            headerName: 'Rebal ID',
            suppressFiltersToolPanel: false,
            field: 'rebalanceResultId',
            colId:'rebalanceResultId', 
            cellRendererSelector:this.cellrendererService.cellRendererSelectorRebalId.bind(this,this.parentComp),
            cellClass: 'irb-cell-content-right',
            minWidth: 75,
            filter: 'agTextColumnFilter',
            suppressFloatingFilterButton: true,
            filterParams: {
              filterOptions: ['contains'],
              clearButton: false,
              debounceMs: 1000,
              textFormatter: this.textFilterFormatter,
              suppressAndOrCondition: true,
            },
          }
        ] 
      };
    }

    getPortfolioColumnGroup(): ColGroupDef {
      return {
        headerName: '',
        suppressFiltersToolPanel: true,
        children: [{
          headerName: 'Portfolio',
          suppressFiltersToolPanel: false,
          field: 'portfolioName',
          colId:'portfolioName', 
          cellRendererSelector:this.editPortfolio.bind(this),
          minWidth: 150,
          filter: 'agTextColumnFilter',
          suppressFloatingFilterButton: true,
          filterParams: {
            filterOptions: ['contains'],
            clearButton: false,
            debounceMs: 1000,
            textFormatter: this.textFilterFormatter,
            suppressAndOrCondition: true,
          }
        }]
      };
    }

    getHiddenColumns(): Array<any> {
      return [{
        // headerName: 'Quick Filters',
        headerName: 'Additional Filters',
        field: 'quickfilter',
        colId: 'quickfilter',
        hide: true,
        suppressColumnsToolPanel: true,
        filter: 'customFilterToolPanel',
      },
      {
        headerName: 'Closed Date',
        suppressFiltersToolPanel: this.current === 'activeOrders' || this.current === 'automatedOrders',
        suppressColumnsToolPanel: true,
        hide: true,
        field: 'modifiedDate',
        colId:'modifiedDate',
        minWidth: 100,
        floatingFilterComponent: 'irbInputFloatingFilterComponent',
        filter: 'rangeDatePicker',
        filterParams: {
          enableArchiveSearch: (this.current === 'resolvedOrders')
        }
      },
      {
        headerName: 'Submitted By',
        suppressFiltersToolPanel: false,
        suppressColumnsToolPanel: true,
        hide: true,
        colId: 'submittedBy',
        field: 'submittedBy',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: (params: any) => {
            params.success(this.submittedByList);
          },
          buttons:['reset', 'apply'],
          newRowsAction: 'keep',
          suppressSorting: true,
          cellHeight: 30,
          closeOnApply: true,          
          refreshValuesOnOpen: true
        },
      },
      {
        headerName: 'Master Account',
        suppressFiltersToolPanel: false,
        suppressColumnsToolPanel: true,
        hide: true,
        colId: 'masterAccount',
        field: 'masterAccount',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: (params: any) => {
            params.success(this.advisorList);
          },
          buttons:['reset', 'apply'],
          newRowsAction: 'keep',
          suppressSorting: true,
          cellHeight: 30,
          closeOnApply: true,          
          refreshValuesOnOpen: true
        },
      },
      {
        headerName: 'External Accounts',
        suppressFiltersToolPanel: false,
        suppressColumnsToolPanel: true,
        hide: true,
        colId: 'extAccOnly',
        field: 'extAccOnly',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['External Accounts Only'],
          suppressMiniFilter: true,
          suppressSelectAll: true,
          //buttons:['reset', 'apply'],
          newRowsAction: 'keep',
          suppressSorting: true,
          cellHeight: 30,
          closeOnApply: true,          
          refreshValuesOnOpen: true
        }
      },{
        headerName: 'Block Orders',
        suppressFiltersToolPanel: false,
        suppressColumnsToolPanel: true,
        hide: true,
        colId: 'blockOrders',
        field: 'blockOrders',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['Block Orders Only'],
          suppressMiniFilter: true,
          suppressSelectAll: true,
        // buttons:['reset', 'apply'],
          newRowsAction: 'keep',
          suppressSorting: true,
          cellHeight: 30,
          closeOnApply: true,          
          refreshValuesOnOpen: true
        }
      }];
  }

  isDisableCheckBox(data: any) {
    if (data) {
      const approvStatus = data.approvalStatus;
      const noOfPortOrders = data.noOfOrders;
      const completedDate = data.completedDate;
      if (approvStatus && approvStatus === "Pending" && noOfPortOrders && noOfPortOrders > 0 && completedDate && completedDate !== "Skipped" && completedDate !== "Processing" && completedDate !== "Queued") {
        return false;
      } else {
        return true;
      }
    }
    return false;
  }
  
  capitalizeFirstLetter(text: string) {
    return text.replace(/_/g, ' ').toLowerCase()
        .split(' ')
        .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
        .join(' ');
  }

  private textFilterFormatter(r: any) {
      return (r == null) ? null :  r;
  }

  private getOrderStatuses() {
    if (this.current === 'activeOrders' || this.current === 'automatedOrders') {
      this.orderStatusList = ['Approved', 'Cancel rejected', 'Cancel requested', 'Error', 'Manually submitted',
        'On hold', 'Overridden', 'Partially filled', 'Pending validation', 'Replace warning', 'Submitted', 'Validated', 'Warning',
        'Allocations failed', 'Exported to ThinkPipes'];
    } else {
      this.orderStatusList = ['Allocations sent', 'Canceled', 'Canceled - partial fill', 'Filled',
        'Marked as executed', 'Manually allocated', 'Mark as Canceled'];
    }
  }

  getSecurityTypes() {
    return new Promise((resolve: any) => {
      this.http.get<string[]>('/irebalone/blotter/sec-type-drop-down', this.httpOptions)
          .subscribe((res: any) => {
            if (res) {
              const data = res.responseData;
              for (let i = 0; i < data.length; i++) {
                if (data[i] !== 'SMA') {
                  data[i] = this.capitalizeFirstLetter(data[i]);
                }
              }
              this.securityTypeList = data;
            }

            resolve();
          });
    });
  }

  private getTicketTypes() {
    if (this.current === 'activeOrders') {
      this.ticketTypeList = ['E', 'M'];
    } else if (this.current === 'automatedOrders') {
      this.ticketTypeList = ['A', 'M'];
    } else {
      this.ticketTypeList = ['A', 'E', 'M'];
    }
  }

  private getTransTypes() {
    this.transTypeList = ['Buy', 'Sell', 'Swap/Exchange', 'Sell to open', 'Sell to close', 'Buy to open', 'Buy to close', 'Spread'];
  }

  getAdvisors() {
    return new Promise((resolve: any) => {
      this.http.get<string[]>('/irebalone/blotter/master-account-drop-down/' + this.current, this.httpOptions).subscribe((res: any) => {
        if (res) {
          this.advisorList = res.responseData || [];
          this.advisorList.sort(this.compare);
        }

        resolve();
      });
    });
  }

  getSubmittedBys()  {
    return new Promise((resolve: any) => {
      this.http.get<string[]>('/irebalone/blotter/submitted-bys', this.httpOptions).subscribe((res: any) => {
        if (res) {
          this.submittedByList = res.responseData || [];
          this.submittedByList.sort(this.compare);
        }

        resolve();
      });
    });
  }

  private compare(a: string, b: string) {
    const A = a.toUpperCase();
    const B = b.toUpperCase();

    let comparison = 0;
    if (A > B) {
      comparison = 1;
    } else if (A < B) {
      comparison = -1;
    }
    return comparison;
  }

  getColumnDefsBlockOrder(parentComp:InventoryComponent) {
    return [
      { 
        headerName: '',
        suppressFiltersToolPanel: true,
        children: [
          {
            headerName: 'Account',
            suppressFiltersToolPanel: true,
            field: 'accountNumber',
            colId:'accountNumber',
            cellRenderer: 'cellRendererFormatter',
            cellClass: 'irb-cell-content-right',
            minWidth: 95,
            filter: 'agTextColumnFilter',
            suppressFloatingFilterButton: true,
            filterParams: {
              // applyButton: true,
              clearButton: true,
              debounceMs: 1000,
              // filterOptions: [],
              textFormatter: this.textFilterFormatter,
              suppressAndOrCondition: true,
              // values: (params: any) => {
              //    params.success(this.modelNames);
              // }
            }
        }]
      },
      { 
        headerName: '',
        suppressFiltersToolPanel: true,
        children: [{
          headerName: 'Account Name',
          suppressFiltersToolPanel: true,
          field: 'accountName',
          colId:'accountName', 
          cellRenderer: 'cellRendererFormatter',
          minWidth: 80,
          filter: 'agTextColumnFilter',
          suppressFloatingFilterButton: true,
          filterParams: {
            clearButton: true,
            debounceMs: 1000,
            textFormatter: this.textFilterFormatter,
            suppressAndOrCondition: true,
          }
        }]
      },
{ 
    headerName: 'Trade Details',
    suppressFiltersToolPanel: true,
    children: [
    {
      headerName: 'Trans Type',
      suppressFiltersToolPanel: true,
      field: 'transactionType',
      colId:'transactionType', 
     valueFormatter: (params: any) => {
          return formatTransaction(params.value);
        },
      minWidth: 75,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: (params: any) => {
          params.success(this.transTypeList);
        },
        valueFormatter: (params: any) => {
          return formatTransaction(params.value);
        },
        buttons:['reset', 'apply'],
        newRowsAction: 'keep',
        suppressSorting: true,
        cellHeight: 30,
        closeOnApply: true,        
        refreshValuesOnOpen: true
      },
    },
    {
      headerName: 'Shr Qty',
      headerComponentParams:{
        irbColInfo: this.inventoryColInfoService.shrQtyColInfo()
      },
      suppressFiltersToolPanel: true,
      field: 'quantity',
      colId:'quantity',
      cellRendererSelector: (params: any) => {
        return this.cellrendererService.formatShareQuantity(params);
      },
      cellClass: 'irb-cell-content-right',
      minWidth: 75,
    },
    {
      headerName: 'Order Amt',
      suppressFiltersToolPanel: true,
      field: 'dollarAmount',
      colId:'dollarAmount', 
      cellRenderer : 'cellRendererFormatter',
      cellRendererParams: { type: 'currency' },
      minWidth: 75,
    }
  ] 
},  
{ 
  headerName: 'Trade Authorization',
  suppressFiltersToolPanel: true,
  children: [
  {
    headerName: 'Sol',
    suppressFiltersToolPanel: true,
    field: 'isSolicited',
    colId:'isSolicited', 
    cellRenderer : 'cellRendererFormatter',
    cellRendererParams: { type: 'checkbox', isCheckboxDisabled: true },
    minWidth: 50,
  },
  {
    headerName: 'Disc',
    suppressFiltersToolPanel: true,
    field: 'isDiscretionary',
    colId:'isDiscretionary', 
    cellRenderer : 'cellRendererFormatter',
    cellRendererParams: { type: 'checkbox', isCheckboxDisabled: true },
    minWidth: 50,
  }
] 
},
{ 
  headerName: 'History',
  suppressFiltersToolPanel: true,
  children: [
  {
    headerName: 'Order History',
    suppressFiltersToolPanel: true,
    field: 'orderHistory',
    colId:'orderHistory',
    cellRendererSelector: (params: any) => {
      return this.cellrendererService.cellRendererSelectorOrderHistory(params,this.parentComp,true)
    },
    minWidth: 60,
  },
  {
    headerName: 'Approval Date',
    suppressFiltersToolPanel: true,
    field: 'createdDate',
    colId:'createdDate', 
    cellRenderer : 'cellRendererFormatter',
    cellRendererParams: {
      type: 'date', format: 'MM/dd/yyyy h:mm a', displayETtext : true
    },
    minWidth: 100,
  },
  {
    headerName: 'Rebal ID',
    suppressFiltersToolPanel: true,
    field: 'rebalanceResultId',
    colId:'rebalanceResultId', 
    cellRendererSelector:this.cellrendererService.cellRendererSelectorRebalId.bind(this,parentComp),
    cellClass: 'irb-cell-content-right',
    minWidth: 75,
  }
] }


];
}

orderStatusRender(params:any){
  const formatValue = formatOrderStatus(params.value);
  if(this.parentComp.current === 'resolvedOrders'){
    return {
        component: "cellRendererFormatter",
        params: {value: formatValue}
    };
  } else {
    return this.orderStatusActionMenu.showActions(params,formatValue, this.parentComp);
  }
}

editPortfolio(params:any){   
  if (params.data) {
    if (params.data.portfolioName !== "Combined" && params.data.portfolioName !== "Unassigned") {
        return {
          component: 'cellRendererFormatter',
          params:{value:params.data.portfolioName,
            link: (params: any) => {
              return (params.value != null && params.value.length > 0);}, 
              onClick: (function (data: any) {
                this.as.fireEvent(IrbClientsEvents.SHOW_EDIT_PORTFOLIO, { data: data });
            }).bind(this)  
            }
          }
      }else {
          return{
          component: 'cellRendererFormatter',
              params:{ value:params.data.portfolioName } 
        }
    }
  }
}

  getExpirationTime(n: number) {
      return this.util.formatEasternDate(n, '/');
  }
}
